import {
  AreaType,
  CompareType,
  EnergyEfficiencyGroup,
  ExpenseType,
  LocationResolutionType,
  ObjectState,
  ObjectType,
  PlaygroundFilter,
  TimeResolution,
} from "@/types";
import { IndexType } from "@/types/IndexType";

export const isTimeResolutionSectionShown = (index: IndexType): boolean =>
  [IndexType.SIM, IndexType.SIPI].includes(index);

export const isEnergyEfficiencyGroupSectionShown = (index: IndexType): boolean => [IndexType.SEPI].includes(index);

export const isExpenseTypeSectionShown = (index: IndexType): boolean =>
  [IndexType.SIPI, IndexType.SIPN].includes(index);

export const isCompareTypeSectionShown = (index: IndexType): boolean => [IndexType.SIM, IndexType.SEPI].includes(index);

export const isObjectStateSectionShown = (index: IndexType): boolean => [IndexType.SIPN].includes(index);

export const getObjectTypeForIndex = (index?: IndexType, expenseType?: ExpenseType): ObjectType[] =>
  index == null
    ? []
    : [
        ObjectType.OneFamilyDwelling,
        ObjectType.OwnerOccupiedDwelling,
        ...(!(index === IndexType.SIPI && expenseType === ExpenseType.Rent) && index !== IndexType.SIPN
          ? [ObjectType.MultiFamilyDwelling]
          : []),
      ];

export const getDefaultPlaygroundFilter = (index?: IndexType | null): PlaygroundFilter => {
  return {
    locationResolution: {
      metadata: {},
      type: LocationResolutionType.PostalCode,
    },
    objectTypes:
      index && index === IndexType.SEPI
        ? [ObjectType.OneFamilyDwelling]
        : [ObjectType.OneFamilyDwelling, ObjectType.MultiFamilyDwelling, ObjectType.OwnerOccupiedDwelling],
    compareType: CompareType.Germany,
    timeResolution: TimeResolution.Quarterly,
    energyEfficiencyGroups: [EnergyEfficiencyGroup.High, EnergyEfficiencyGroup.Medium, EnergyEfficiencyGroup.Low],
    expenseType: ExpenseType.Buy,
    objectStates: [ObjectState.NewBuilding],
  };
};

export const getDefaultFilterForIndex = (index?: IndexType): PlaygroundFilter => {
  if (index == null) {
    return getDefaultPlaygroundFilter();
  }

  const adjustedFilter: PlaygroundFilter = { ...getDefaultPlaygroundFilter(index) };
  const availableObjectTypes = getObjectTypeForIndex(index);
  adjustedFilter.objectTypes = adjustedFilter.objectTypes.filter((type) => availableObjectTypes.includes(type));

  if (!isEnergyEfficiencyGroupSectionShown(index)) {
    adjustedFilter.energyEfficiencyGroups = undefined;
  }

  if (!isExpenseTypeSectionShown(index)) {
    adjustedFilter.expenseType = undefined;
  }

  if (!isCompareTypeSectionShown(index)) {
    adjustedFilter.compareType = undefined;
  }

  if (!isTimeResolutionSectionShown(index)) {
    adjustedFilter.timeResolution = undefined;
  }

  if (index === IndexType.SIPI || index === IndexType.SIPN) {
    adjustedFilter.locationResolution = {
      type: LocationResolutionType.PostalCode,
      metadata: {
        postalCodes: ["10115"],
      },
    };
  }

  if (index === IndexType.SIPI) {
    adjustedFilter.timeResolution = TimeResolution.Quarterly;
  }

  if (index === IndexType.SIM) {
    adjustedFilter.timeResolution = TimeResolution.Yearly;
  }

  if (index === IndexType.SEPI) {
    adjustedFilter.compareType = CompareType.COUNTRY;
    adjustedFilter.areaType = AreaType.ALL;
  }

  if (!isObjectStateSectionShown(index)) {
    adjustedFilter.objectStates = undefined;
  }

  return adjustedFilter;
};

export const isFilterMissingValues = (filter: PlaygroundFilter): boolean => {
  return (
    filter.locationResolution == null ||
    (filter.locationResolution.type === LocationResolutionType.PostalCode &&
      (filter.locationResolution.metadata.postalCodes?.length ?? 0) === 0)
  );
};
